import { useMemo, type ReactElement } from 'react';
import type { TimelineSpan } from '../../api/admin.js';
import { kindColor } from '../../lib/spanKind.js';
import { buildTree, flattenTree } from './buildTree.js';
import { formatDurationMs, nanoToUsdCompact } from '../../lib/format.js';
import { preferRawCost } from '../../lib/cost-display.js';

interface TreeProps {
  spans: ReadonlyArray<TimelineSpan>;
  selectedId: string | null;
  onSelect: (spanId: string) => void;
}

export function Tree({ spans, selectedId, onSelect }: TreeProps): ReactElement {
  const rows = useMemo(() => flattenTree(buildTree(spans)), [spans]);
  if (rows.length === 0) {
    return <div className="empty">Sin spans</div>;
  }
  return (
    <div data-testid="tree">
      {rows.map(({ span, depth }) => {
        const isSelected = selectedId === span.id;
        return (
          <div
            key={span.id}
            className={`tree-row ${isSelected ? 'is-selected' : ''}`}
            style={{ ['--depth' as never]: depth }}
            onClick={() => onSelect(span.id)}
            data-testid={`tree-row-${span.id}`}
          >
            <span className="tree-label">
              <span style={{ color: kindColor(span.kind) }}>● </span>
              {span.kind} · {span.name}
              {span.isError ? <span className="badge b-error" style={{ marginLeft: 6 }}>error</span> : null}
            </span>
            <span className="tree-meta">{formatDurationMs(span.durationMs)}</span>
            <span className="tree-meta">
              {(() => {
                const cost = preferRawCost(span.providerCostNano, span.costNano);
                return cost ? nanoToUsdCompact(cost) : '—';
              })()}
            </span>
          </div>
        );
      })}
    </div>
  );
}
